import toast from 'react-hot-toast'
import { useMutation } from '@tanstack/react-query'

import useQuery from '@shared/hooks/src/useQuery'
import { useMe } from '@shared/providers/src/MeProvider'
import { queryClient, setQueriesData, setQueryData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { filterCache, handleError, mapCache, QK } from '@shared/utils'

export function useSOP(id, options = {}) {
  return useQuery({
    queryKey: QK.sops.id(id).details,
    queryFn: () => API.sops.id(id).details(),
    ...options,
  })
}

export function useSOPCreate() {
  return useMutation({
    mutationFn: (data) => API.sops.create(data, { skipHandling: true }),
    onSuccess: (sop) => {
      setQueryData(QK.sops.id(sop.id).details, sop)
      queryClient.invalidateQueries({ queryKey: QK.sops.lists })
    },
    onError: handleError,
  })
}

export function useSOPEdit(id) {
  return useMutation({
    mutationFn: (data) => API.sops.id(id).update(data, { skipHandling: true }),
    onSuccess: (updatedSop) => {
      setQueryData(QK.sops.id(id).details, updatedSop)
      setQueriesData(
        { queryKey: QK.sops.lists },
        mapCache((sop) => (sop.id === id ? updatedSop : sop))
      )
    },
    onError: handleError,
  })
}

export function useSOPDelete(id) {
  return useMutation({
    mutationFn: () => API.sops.id(id).remove(),
    onSuccess: () =>
      setQueriesData(
        { queryKey: QK.sops.lists },
        filterCache((sop) => sop.id !== id)
      ),
    onError: handleError,
  })
}

export function useSOPNotificationCreation(id) {
  const me = useMe()

  return useMutation({
    mutationFn: (data) => API.sops.id(id).notifications.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QK.users.id(me.id).sopNotifications.lists })
      return toast('Corresponding users were notified')
    },
    onError: (error) => handleError(error, { showResponse: true }),
  })
}

export function useSOPNotificationRead() {
  const me = useMe()

  return useMutation({
    mutationFn: (id) => API.users.id(me.id).sopNotifications.id(id).read(),
    onSuccess: (response) => {
      setQueriesData(
        { queryKey: QK.users.id(me.id).sopNotifications.lists },
        mapCache((notification) => (response.id === notification.id ? response : notification))
      )
    },
    onError: (error) => handleError(error, { showResponse: true }),
  })
}
